import { Link } from "react-router-dom";
import { Check, ArrowRight, FileText, ShieldCheck, Leaf, Wrench } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { FadeUp } from "../components/Reveal";
import { FAQS, IMAGES, SPACES, COMMITMENT, COMPLIANCE } from "../data/site";

const commitIcons = [FileText, ShieldCheck, Leaf, Wrench];

/* Split: dark "Our Promise" image panel + light "Our Commitment" grid */
const PromiseSplit = () => (
  <section className="grid grid-cols-1 lg:grid-cols-2" data-testid="promise-split">
    {/* Left dark */}
    <div className="relative min-h-[520px] lg:min-h-[720px] overflow-hidden bg-[#241a12]">
      <img src={IMAGES.promiseScene} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1c140d] via-[#1c140d]/55 to-[#1c140d]/25" />
      <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-16 py-24">
        <FadeUp>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-white">Our<br />Promise</h1>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-6 text-sm md:text-base uppercase tracking-[0.24em] text-gold">Safety. Compliance. Peace of mind.</p>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-[#e0d4c2] leading-relaxed max-w-md">
            With The Scent Butler, safety and compliance are at the heart of every installation.
            Our commercial scenting systems are designed, manufactured and tested to meet Australian
            regulations and global industry standards — so you can focus on what matters most: your business.
          </p>
        </FadeUp>
      </div>
    </div>

    {/* Right light */}
    <div className="bg-[#ece3d5] px-8 md:px-16 py-20 lg:py-24 flex flex-col justify-center" data-testid="commitment-section">
      <FadeUp>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2a1d12] mb-3">Our Commitment</h2>
        <span className="block w-16 h-px bg-gold mb-12" />
      </FadeUp>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
        {COMMITMENT.map((c, i) => {
          const Icon = commitIcons[i] || Check;
          return (
            <FadeUp key={c.title} delay={(i % 2) * 0.1} data-testid={`commitment-${i}`}>
              <span className="grid place-items-center w-14 h-14 rounded-full ring-1 ring-[#c4b59d] mb-5">
                <Icon size={22} strokeWidth={1.2} className="text-[#4a3c2b]" />
              </span>
              <h3 className="font-sans text-sm font-semibold uppercase tracking-[0.12em] text-[#2a1d12] mb-2">{c.title}</h3>
              <p className="text-sm text-[#6a5a45] leading-relaxed">{c.body}</p>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const CommercialCompliance = () => (
  <section className="bg-[#f0e8db]" data-testid="compliance-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <FadeUp>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2a1d12]">Commercial Compliance</h2>
        <span className="block w-16 h-px bg-gold mt-6 mb-6" />
        <p className="text-[#6a5a45] max-w-xl leading-relaxed">
          We understand that many businesses require documentation for workplace health and safety
          requirements.
        </p>
      </FadeUp>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mt-14 items-start">
        <div>
          {COMPLIANCE.map((c, i) => (
            <FadeUp key={c} delay={i * 0.06} className="flex items-center gap-4 py-4 border-t border-[#d8cbb8]" data-testid={`compliance-item-${i}`}>
              <span className="grid place-items-center w-7 h-7 rounded-full ring-1 ring-[#c4b59d] shrink-0">
                <Check size={13} className="text-[#4a3c2b]" />
              </span>
              <span className="text-[#3a2e20]">{c}</span>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.15}>
          <div className="rounded-2xl ring-1 ring-[#d8cbb8] bg-[#e6dccb] px-8 md:px-12 py-14 text-center">
            <span className="grid place-items-center w-16 h-16 rounded-full ring-1 ring-[#c4b59d] mx-auto mb-6">
              <ShieldCheck size={30} strokeWidth={1.1} className="text-[#4a3c2b]" />
            </span>
            <h3 className="font-serif text-2xl md:text-3xl text-[#2a1d12]">Peace of Mind for Your Business</h3>
            <span className="block w-12 h-px bg-gold mt-5 mb-5 mx-auto" />
            <p className="text-[#6a5a45] leading-relaxed max-w-md mx-auto">
              Whether you're scenting a hotel, showroom, retail store, office or wellness space, The
              Scent Butler provides professional fragrance solutions backed by the documentation and
              support your business expects.
            </p>
            <Link to="/enquiries" data-testid="compliance-cta"
              className="group inline-flex items-center gap-3 mt-8 px-8 py-4 bg-[#2a1d12] text-[#f0e6d5] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#3a2a1a] transition-colors duration-300">
              Request a Consultation
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="bg-cream" data-testid="about-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <FadeUp className="relative aspect-[4/5] overflow-hidden border border-cream-border order-2 lg:order-1">
        <img src={IMAGES.catMio1} alt="The Scent Butler" className="w-full h-full object-cover" />
      </FadeUp>
      <div className="order-1 lg:order-2">
        <FadeUp><p className="eyebrow mb-6">About The Scent Butler</p></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink leading-tight">
            Australian-owned, <span className="italic">obsessed</span> with detail.
          </h2>
          <span className="block w-16 h-px bg-gold mt-6" />
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-ink-muted leading-relaxed mt-8">
            The Scent Butler is a commercial scenting company built for businesses that understand
            atmosphere is everything. We blend premium fragrance oils, engineer waterless cold-air
            diffusion technology, and provide fast, local support across Australia.
          </p>
        </FadeUp>
        <FadeUp delay={0.3}>
          <p className="text-ink-muted leading-relaxed mt-4">
            From boutique salons to five-star hotels, we help businesses smell exactly the way
            they want to be remembered.
          </p>
        </FadeUp>
      </div>
    </div>
  </section>
);

const SpacesWeService = () => (
  <section className="bg-cream-alt" data-testid="spaces-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <FadeUp>
          <p className="eyebrow mb-5">Where we work</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink leading-tight">
            Spaces we <span className="italic">transform.</span>
          </h2>
          <span className="block w-16 h-px bg-gold mt-6" />
          <Link to="/enquiries" data-testid="spaces-cta"
            className="group inline-flex items-center gap-3 mt-9 px-8 py-4 bg-gold text-[#1a120b] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#e4d2ab] transition-colors duration-300">
            Discuss your space
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </FadeUp>
      </div>
      <div className="lg:col-span-7">
        <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-cream-border">
          {SPACES.map((sp, i) => (
            <FadeUp key={sp} delay={(i % 2) * 0.08}
              className="flex items-center gap-4 py-5 border-b border-cream-border" data-testid={`space-${i}`}>
              <Check size={16} className="text-gold shrink-0" />
              <span className="font-serif text-xl text-ink">{sp}</span>
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section className="bg-cream" data-testid="faq-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <FadeUp>
          <p className="eyebrow mb-5">FAQ</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink leading-tight">Questions, answered.</h2>
          <span className="block w-16 h-px bg-gold mt-6" />
          <p className="text-ink-muted mt-6 leading-relaxed">
            Can't find what you're looking for?{" "}
            <Link to="/enquiries" className="text-gold link-underline">Get in touch.</Link>
          </p>
        </FadeUp>
      </div>
      <div className="lg:col-span-8">
        <FadeUp>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-cream-border" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-ink hover:text-gold hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink-muted text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </div>
    </div>
  </section>
);

export default function Info() {
  return (
    <div data-testid="info-page">
      <div className="pt-20" />
      <PromiseSplit />
      <CommercialCompliance />
      <About />
      <SpacesWeService />
      <Faq />
    </div>
  );
}
